import axios from 'axios';
import { prepareBrowserRequest } from './browserSession';


// Crear una instancia de axios con la URL base relativa
const api = axios.create({
  baseURL: '/api', // Usar una ruta relativa para que el proxy funcione
  headers: {
    'Content-Type': 'application/json',
  },
  // Añadir un timeout más holgado para producción
  timeout: 30000,
});

api.interceptors.request.use(prepareBrowserRequest);

// Interceptor para manejar errores
api.interceptors.response.use(
  (response) => response,
  (error) => {
    // Manejar errores específicos
    if (error.response) {
      // El servidor respondió con un código de estado fuera del rango 2xx
      console.error(`API Error: ${error.response.status} - ${error.response.statusText}`);

      const responseCode = error.response.data?.code;
      const requestUrl = error.config?.url || '';
      const skipAuthRedirect = error.config?.skipAuthRedirect;
      const isAuthEndpoint = ['/login/', '/google-login/', '/register/', '/collabs/register-invited/']
        .some(endpoint => requestUrl.endsWith(endpoint));
      const isIntegrationAuthError = responseCode === 'token_expired'
        || requestUrl.includes('my_instagram_media')
        || requestUrl.includes('/instagram/');

      // Si es un error 401 real de FOTH, limpiar el token y redirigir al login.
      // Los 401 de integraciones externas (Instagram) se manejan en la pantalla actual.
      const publicPath = window.location.pathname.startsWith('/i/');
      if (error.response.status === 401 && !skipAuthRedirect && !isIntegrationAuthError && !isAuthEndpoint && !publicPath) {
        localStorage.removeItem('token');
        window.location.href = '/login';
      }
    } else if (error.request) {
      // La solicitud se hizo pero no se recibió respuesta
      console.error('API Error: No response received', error.request);
    } else {
      // Algo sucedió en la configuración de la solicitud que desencadenó un error
      console.error('API Error:', error.message);
    }

    return Promise.reject(error);
  }
);

// NO configurar el token aquí, ya que esto solo se ejecuta una vez al cargar la aplicación
// En su lugar, lo configuramos en AuthContext.js

// Funciones para interactuar con la API
export const login = async (username, password) => {
  return api.post('/login/', { username, password }, { skipAuthRedirect: true });
};

export const googleLogin = async (token, allowRegistration = false) => {
  return api.post('/google-login/', { token, allow_registration: allowRegistration }, { skipAuthRedirect: true });
};

export const googleLink = async (token, password) => {
  return api.post('/google-link/', { token, password }, { skipAuthRedirect: true });
};

export const googleUnlink = async () => api.post('/google-unlink/');

export const logout = async () => {
  return api.post('/logout/');
};

export const setUserType = async (userType) => {
  return api.post('/set-user-type/', { user_type: userType });
};

export const getUserProfile = async () => api.get('/profile/', { skipAuthRedirect: true });

export const getUserProfileByUsername = async (username) => {
  try {
    // Try with the configured instance first
    let response = await api.get(`/profile/${username}/`);

    return response;
  } catch (error) {
    console.error('Error in getUserProfileByUsername:', error);
    throw error;
  }
};

export const getCreators = async (params = {}) => {
  return api.get('/creators/search/', { params });
};


export const getAdminDashboard = async () => {
  return api.get('/admin-dashboard/');
};

export const getAdminMetaOperations = async () => {
  return api.get('/admin/instagram/operations/');
};

export const getAdminInstagramCollabOps = async (collabId) => {
  return api.get(`/admin/instagram/collabs/${collabId}/ops/`);
};

export const refreshAdminInstagramCollabOps = async (collabId, limit = 10, mode = 'all') => {
  return api.post(`/admin/instagram/collabs/${collabId}/ops/refresh/`, { limit, mode });
};

export const refreshAdminInstagramPostOps = async (postId) => {
  return api.post(`/admin/instagram/posts/${postId}/ops/refresh/`);
};

export const updateAdminMetaOperationsPause = async ({ action, reason, durationMinutes }) => {
  return api.post('/admin/instagram/operations/pause/', {
    action,
    reason,
    duration_minutes: durationMinutes,
  });
};

export const updateVipRegistrationMode = async (enabled) => {
  return api.post('/admin/registration-mode/vip/', { enabled });
};

export const getAdminUsers = async (params = {}) => {
  return api.get('/admin/users/', { params });
};

export const deleteAdminUser = async (userId) => {
  return api.delete(`/admin/users/${userId}/`);
};

export const getAdminSellers = async () => {
  return api.get('/admin/sellers/');
};

export const createAdminSeller = async (sellerData) => {
  return api.post('/admin/sellers/', sellerData);
};

export const getAdminAffiliateSales = async (params = {}) => {
  return api.get('/payments/admin/affiliate-sales/', { params });
};

export const getSellerReferral = async (code) => {
  return api.get(`/partners/${code}/`, { skipAuthRedirect: true });
};

export const getSellerDashboard = async () => {
  return api.get('/payments/seller/dashboard/');
};

export const getCreatorWallet = async () => api.get('/creator/wallet/');

export const quoteCreatorWithdrawal = async (amount) => (
  api.post('/creator/withdrawals/quote/', amount ? { amount } : {})
);

export const confirmCreatorWithdrawal = async (withdrawalId) => (
  api.post(`/creator/withdrawals/${withdrawalId}/confirm/`, { confirm: true })
);

export const retryCreatorWithdrawal = async (withdrawalId) => (
  api.post(`/creator/withdrawals/${withdrawalId}/retry/`)
);

export const startCreatorStripeOnboarding = async () => (
  api.post('/creator/stripe/onboarding/')
);

export const startCreatorGlobalPayoutsOnboarding = async (payload = {}) => (
  api.post('/creator/global-payouts/onboarding/', payload)
);

export const getUserDashboardMetrics = async () => {
  return api.get('/dashboard/metrics/');
};

export const getCategories = async (type = 'all') => {
  return api.get(`/categories/?type=${type}`);
};

export const getCountries = async () => {
  try {
    // Usar la instancia de api para consistencia y manejo de errores
    const response = await api.get('/countries/');
    return response;
  } catch (error) {
    console.error('Error in getCountries:', error);
    throw error;
  }
};

export const getProvinces = async (countryId) => {
  try {
    const response = await api.get(`/countries/${countryId}/provinces/`);
    return response;
  } catch (error) {
    console.error('Error in getProvinces:', error);
    throw error;
  }
};

export const checkAvailability = async (usernameOrPayload, email) => {
  if (typeof usernameOrPayload === 'object' && usernameOrPayload !== null) {
    return api.post('/check-availability/', usernameOrPayload);
  }

  if (!email && typeof usernameOrPayload === 'string' && usernameOrPayload.includes('@')) {
    return api.post('/check-availability/', { email: usernameOrPayload });
  }

  return api.post('/check-availability/', { username: usernameOrPayload, email });
};

export const register = async (userData) => {
  return api.post('/register/', userData);
};

export const registerCreator = async (userData) => {
  if (userData instanceof FormData) {
    if (!userData.has('user_type')) {
      userData.append('user_type', 'creator');
    }
    return register(userData);
  }
  return register({ ...userData, user_type: 'creator' });
};

export const registerBusiness = async (userData) => {
  if (userData instanceof FormData) {
    if (!userData.has('user_type')) {
      userData.append('user_type', 'business');
    }
    return register(userData);
  }
  return register({ ...userData, user_type: 'business' });
};

export const verifyAccount = async (userId, code) => {
  return api.post('/verify-account/', { user_id: userId, code });
};

export const resendVerificationCode = async (userId) => {
  return api.post('/resend-verification-code/', { user_id: userId });
};

export const requestPasswordReset = async (email) => {
  return api.post('/password-reset-request/', { email });
};

export const resetPassword = async (userId, code, password) => {
  return api.post('/password-reset-confirm/', { user_id: userId, code, new_password: password });
};

export const submitDataDeletionRequest = async (requestData) => {
  return api.post('/data-deletion-request/', requestData, { skipAuthRedirect: true });
};

export const submitContactRequest = async (requestData) => {
  return api.post('/contact-request/', requestData, { skipAuthRedirect: true });
};

export const submitPartnerApplication = async (applicationData) => {
  return api.post('/partner-applications/', applicationData, { skipAuthRedirect: true });
};

export const updateUserProfile = async (userData) => {
  const config = userData instanceof FormData ? {
    headers: { 'Content-Type': undefined }
  } : {};

  return api.put('/profile/update/', userData, config);
};

// Funciones para verificación de negocios
export const checkBusinessVerification = async () => {
  return api.get('/business/verification/check/');
};

export const checkFirstCollabStatus = async () => {
  return api.get('/business/first-collab-status/');
};

export const getPendingBusinessVerifications = async (params = {}) => {
  return api.get('/business/verification/pending/', { params });
};

export const approveBusinessVerification = async (businessId) => {
  return api.post(`/business/verification/approve/${businessId}/`);
};

export const rejectBusinessVerification = async (businessId, reason) => {
  return api.post(`/business/verification/reject/${businessId}/`, { reason });
};

// Funciones para verificación de creadores
export const checkCreatorVerification = async () => {
  return api.get('/creator/verification/check/');
};

export const getPendingCreatorVerifications = async (params = {}) => {
  return api.get('/creator/verification/pending/', { params });
};

/**
 * Aprueba la verificación de un creador.
 * @param {string|number} creatorId - ID del creador a aprobar
 * @param {Object} data - Datos adicionales (creator_type)
 * @returns {Promise} - Respuesta de la API
 */
export const approveCreator = async (creatorId, data = {}) => {
  return api.post(`/creator/verification/approve/${creatorId}/`, data);
};

export const rejectCreator = async (creatorId, { reason }) => {
  return api.post(`/creator/verification/reject/${creatorId}/`, { reason });
};

export const submitCreatorVerificationDocuments = async (formData) => {
  return api.post('/creator/verification/submit/', formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });
};

// Función para crear ofertas
export const createOffer = async (offerData) => {
  return api.post('/offers/create/', offerData);
};

export const submitVerificationDocuments = async (formData) => {
  return api.post('/business/verification/submit/', formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });
};

// Funciones para reservaciones
export const getReservations = (params = {}) => {
  return api.get('/reservations/', { params });
};

export const createReservation = (data) => {
  return api.post('/reservations/', data);
};

export const updateReservationStatus = (id, status) => {
  return status === 'cancelled'
    ? api.patch(`/reservations/${id}/`, { status })
    : api.post(`/reservations/${id}/update_status/`, { status });
};

// Funciones para aplicaciones
export const getApplications = (params = {}) => {
  return api.get('/feed/applications/', { params });
};

export const getBusinessPendingApplicants = async () => {
  return api.get('/feed/applications/pending-review/');
};

export const getCreatorApplicationDecisions = async () => {
  return api.get('/feed/applications/decisions/');
};

export const createApplication = (offerId, data = {}) => {
  return api.post(`/feed/${offerId}/apply/`, data);
};

export const getFeedOfferDetail = (offerId) => {
  return api.get(`/feed/${offerId}/`);
};

export const getCreatorJourneyStatus = (offerId) => {
  return api.get(`/feed/${offerId}/journey-status/`);
};

export const getOfferDetails = (offerId) => {
  return api.get(`/offers/collab/${offerId}/`);
};

export const updateApplicationStatus = (id, status) => {
  return api.patch(`/feed/applications/${id}/update_status/`, { status });
};

// ─────────────────────────────────────────────────────────────
//  Invited Creator / Verification Access Control
// ─────────────────────────────────────────────────────────────

/** Register a new creator via invite code (public) */
export const registerInvitedCreator = async (userData) => {
  return api.post('/collabs/register-invited/', userData);
};

export const acceptRecipientInvite = async (inviteToken) => {
  return api.post('/collabs/accept-recipient-invite/', { invite_token: inviteToken });
};

export const declineRecipientInvite = async (inviteToken) => {
  return api.post('/collabs/decline-recipient-invite/', { invite_token: inviteToken });
};

/** Get list of invited creators (admin only) */
export const getInvitedCreatorVerifications = async (params = {}) => {
  return api.get('/creator/verification/invited/', { params });
};

export const sendRecipientInvites = async (collabId, emails, expiryDays = 7) => {
  return api.post(`/collabs/collab/${collabId}/invite_creator/`, { emails, expiry_days: expiryDays });
};

export const cancelCollabOffer = async (collabId) => {
  return api.post(`/collabs/collab/${collabId}/cancel/`);
};

export const getRecipientInvitations = async (collabId) => {
  return api.get(`/collabs/collab/${collabId}/recipient_invitations/`);
};

/** Get registered creator invitations for the business collab journey. */
export const getDirectInvitations = async (collabId) => {
  return api.get(`/collabs/collab/${collabId}/direct_invitations/`);
};

export const getMyRecipientInvitations = async () => {
  return api.get('/collabs/my-recipient-invitations/');
};

export const revokeRecipientInvitation = async (collabId, invitationId) => {
  return api.post(`/collabs/collab/${collabId}/revoke_recipient_invitation/`, { invitation_id: invitationId });
};

/** Send direct invite to a registered creator (business owner) */
export const inviteRegisteredCreator = async (collabId, creatorId) => {
  return api.post(`/collabs/collab/${collabId}/invite_registered_creator/`, { creator_id: creatorId });
};

export const getRecipientInviteInfo = async (inviteToken) => {
  return api.get(`/collabs/collab/invite/${inviteToken}/`);
};

/** Fetch active business offers */
export const getBusinessOffers = async (params = {}) => {
  return api.get('/collabs/collab/', { params });
};

/** Duplicate a business collab as a draft */
export const duplicateCollabOffer = async (id) => {
  return api.post(`/collabs/collab/${id}/duplicate/`);
};

/** Publish a draft/pending collab with an existing subscription */
export const publishCollabOffer = async (id, data = {}) => {
  return api.post(`/collabs/collab/${id}/publish/`, data);
};

/** Fetch collab summary for business */
export const getCollabSummary = async (id) => {
  return api.get(`/collabs/collab/${id}/summary/`);
};

/** Fetch the business-side creator pipeline for a collab. */
export const getCollabApplications = async (id) => {
  return api.get('/feed/applications/', { params: { offer: id } });
};

/** Complete one accepted creator after the business approves its delivery. */
export const completeCollabApplication = async (applicationId, data = {}) => {
  return api.post(`/feed/applications/${applicationId}/complete/`, data);
};

/** Let an in-kind creator confirm completion after registering all deliverables. */
export const confirmCreatorApplicationCompletion = async (applicationId, data = {}) => {
  return api.post(`/feed/applications/${applicationId}/confirm-completion/`, data);
};

/** Fetch collab invite info/stats */
export const getCollabInviteInfo = async (id) => {
  return api.get(`/collabs/collab/${id}/invite_info/`);
};

/** Get direct invitations for creator */
export const getCreatorInvitations = async () => {
  return api.get('/collabs/invitations/');
};

/** Respond to a direct invitation */
export const respondToInvitation = async (id, action) => {
  return api.post(`/collabs/invitations/${id}/respond/`, { action });
};

/** Fetch collab performance metrics (business owner) */
export const getCollabMetrics = async (id) => {
  return api.get(`/collabs/collab/${id}/metrics/`);
};

/** Fetch the creator's private, creator-scoped EMV report */
export const getCreatorEmvReport = async (id) => {
  return api.get(`/collabs/collab/${id}/creator-report/`);
};

export const refreshLatestCollabMetrics = async (id, limit = 25) => {
  return api.post(`/collabs/collab/${id}/metrics/refresh-latest/`, { limit });
};

/** Fetch Creator EMV report JSON (business owner/admin) */
export const getCollabReport = async (id) => {
  return api.get(`/collabs/collab/${id}/report/`);
};

/** Generate/refresh Creator EMV report snapshot */
export const generateCollabReport = async (id) => {
  return api.post(`/collabs/collab/${id}/report/generate/`);
};

/** Download Creator EMV report */
export const downloadCollabReport = async (id, format = 'pdf') => {
  return api.get(`/collabs/collab/${id}/report/download/`, {
    params: { format },
    responseType: 'blob',
  });
};

export const getStripeConfig = async () => {
  return api.get('/stripe/config/');
};

export const getWebPushPublicKey = async () => {
  return api.get('/push/public-key/', { skipAuthRedirect: true });
};

export const saveWebPushSubscription = async (subscription) => {
  return api.post('/push/subscriptions/', subscription);
};

export const deleteWebPushSubscription = async (endpoint) => {
  return api.delete('/push/subscriptions/', { data: { endpoint } });
};

export const createStripeCheckoutSession = async ({ planId, offerId = null, affiliateCode = null }) => {
  return api.post('/stripe/checkout-session/', {
    plan_id: planId,
    offer_id: offerId,
    affiliate_code: affiliateCode,
  });
};

export const confirmStripeCheckoutSession = async (sessionId) => {
  return api.post('/stripe/checkout-session/confirm/', { session_id: sessionId });
};

export const getCollabPaymentStatus = async (collabId) => {
  return api.get(`/collab-payments/${collabId}/`, { skipAuthRedirect: true });
};

export const getPaidCollabFlowConfig = async () => api.get('/collab-payments/config/');

export const createCollabPaymentCheckout = async (collabId, data) => {
  return api.post(`/collab-payments/${collabId}/checkout/`, data);
};

export const quoteCollabPaymentMethod = async (collabId, data) => {
  return api.post(`/collab-payments/${collabId}/payment-method/`, data);
};

export const createCollabPaymentIntent = async (collabId, data) => {
  return api.post(`/collab-payments/${collabId}/intent/`, data);
};

export const approveCreatorAllocation = async (allocationId) => {
  return api.post(`/creator-allocations/${allocationId}/approve/`);
};

export const reportCreatorAllocationIssue = async (allocationId, data) => {
  return api.post(`/creator-allocations/${allocationId}/report-issue/`, data);
};

export const requestCreatorAllocationRefund = async (allocationId, data) => {
  return api.post(`/creator-allocations/${allocationId}/refund-requests/`, data);
};

export const requestCollabPositionReduction = async (collabId, data) => {
  return api.post(`/collab-payments/${collabId}/position-reductions/`, data);
};

/** Fetch business deliverables that still need review */
export const getBusinessPendingDeliveries = async () => {
  return api.get('/collabs/collab/pending_deliveries/');
};

/** Submit a deliverable for a collab (creator) */
export const submitCollabPost = async (id, postData) => {
  return api.post(`/collabs/collab/${id}/submit_post/`, postData);
};

/** Withdraw a submitted deliverable for a collab (creator) */
export const withdrawCollabPost = async (id, postData) => {
  return api.post(`/collabs/collab/${id}/withdraw_post/`, postData);
};

// Instagram Endpoints
export const getInstagramAccounts = async (sessionKey) => {
  return api.get(`/accounts/instagram/accounts/?session_key=${sessionKey}`);
};

export const selectInstagramAccount = async (sessionKey, instagramBusinessId) => {
  return api.post('/accounts/instagram/select-account/', {
    session_key: sessionKey,
    instagram_business_id: instagramBusinessId
  });
};

export const disconnectInstagram = async () => {
  return api.post('/instagram/disconnect/');
};


// Exportar la instancia de axios por defecto
export default api;
